import { createInertiaApp, router } from "@inertiajs/vue3";
import "vite/modulepreload-polyfill";
import { createApp, type DefineComponent, h } from "vue";

import "./styles/index.scss";
// Iconos Phosphor para los logros/insignias (achievement.icon = "ph-...").
import "@phosphor-icons/web/regular";

// OJO: esta lista está triplicada y debe mantenerse sincronizada con
// _base.scss (html.mm-reveal-preload) y con la condición por ruta de
// website/templates/layout.html (clase mm-reveal-preload en <html>).
const GLOBAL_REVEAL_SELECTOR = [
  ".content > section",
  ".content > .container",
  ".content > .master-block",
  ".container__header",
  ".container > .block",
  ".block-container > .block",
  ".block-container > article",
  ".block-container > a",
  ".subject-card",
  ".branch-card",
  ".specialization-card",
  ".pack-card",
  ".plan-card",
  ".faq-container",
  ".comparison-table",
].join(",");

const GLOBAL_REVEAL_EXCLUDED_SELECTOR = [
  ".modal",
  ".modal-container",
  ".notification",
  ".no-motion",
  "[aria-modal='true']",
].join(",");
const GLOBAL_REVEAL_DELAY_MS = 0;

function setupGlobalReveal() {
  const app = document.getElementById("app");
  if (!app) return;

  const reduceMotionQuery = window.matchMedia("(prefers-reduced-motion: reduce)");
  let observer: IntersectionObserver | undefined;
  let scanQueued = false;

  // Public site pages (everything under MainLayout, rooted at #site) opt out of
  // the global reveal so the landings paint instantly. The Mastermind Pro
  // landing lives under #site too but keeps its own hero animation, which is
  // self-contained in its component — the global reveal never drove it.
  const skipGlobalReveal = () => Boolean(document.querySelector("#site"));
  const removePreloadGuard = () => {
    document.documentElement.classList.remove("mm-reveal-preload");
  };

  const makeVisible = (element: HTMLElement) => {
    element.classList.add("mm-reveal", "mm-reveal--visible");
    element.style.removeProperty("--mm-reveal-delay");
  };

  const createObserver = () => {
    observer?.disconnect();
    observer = undefined;

    if (reduceMotionQuery.matches || !("IntersectionObserver" in window)) return;

    observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add("mm-reveal--visible");
          observer?.unobserve(entry.target);
        });
      },
      // threshold must stay 0: blocks taller than ~8x the viewport (e.g. the
      // full course/pack lists, which stack every item in a single MasterBlock)
      // can never reach a positive ratio, so a non-zero threshold would leave
      // them permanently at opacity:0. Fire as soon as any pixel is on screen.
      { threshold: 0, rootMargin: "0px 0px -7% 0px" },
    );
  };

  const getTargets = () => (
    Array.from(document.querySelectorAll<HTMLElement>(GLOBAL_REVEAL_SELECTOR))
      .filter((element) => !element.classList.contains("mm-reveal"))
      .filter((element) => !element.classList.contains("reveal"))
      .filter((element) => !element.closest(GLOBAL_REVEAL_EXCLUDED_SELECTOR))
  );

  const scan = () => {
    if (skipGlobalReveal()) {
      removePreloadGuard();
      return;
    }

    const targets = getTargets();
    if (reduceMotionQuery.matches || !observer) {
      targets.forEach(makeVisible);
      removePreloadGuard();
      return;
    }

    targets.forEach((element) => {
      element.classList.add("mm-reveal");
      if (GLOBAL_REVEAL_DELAY_MS > 0) {
        element.style.setProperty(
          "--mm-reveal-delay",
          `${GLOBAL_REVEAL_DELAY_MS}ms`,
        );
      } else {
        element.style.removeProperty("--mm-reveal-delay");
      }
      observer?.observe(element);
    });
    removePreloadGuard();
  };

  const scheduleScan = () => {
    if (scanQueued) return;
    scanQueued = true;
    window.requestAnimationFrame(() => {
      scanQueued = false;
      scan();
    });
  };

  createObserver();
  scheduleScan();

  // Safety net: never let content stay hidden. If a block was marked
  // `mm-reveal` (opacity:0) but the observer never revealed it while it is
  // already within the viewport, force it visible. Below-the-fold blocks are
  // left untouched so they still animate in on scroll.
  const revealStuckInViewport = () => {
    const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
    document
      .querySelectorAll<HTMLElement>(".mm-reveal:not(.mm-reveal--visible)")
      .forEach((element) => {
        const rect = element.getBoundingClientRect();
        if (rect.top < viewportHeight && rect.bottom > 0) {
          element.classList.add("mm-reveal--visible");
        }
      });
  };
  window.setTimeout(revealStuckInViewport, 1200);

  const mutationObserver = new MutationObserver((mutations) => {
    if (mutations.some((mutation) => mutation.addedNodes.length > 0)) {
      scheduleScan();
    }
  });
  mutationObserver.observe(app, { childList: true, subtree: true });

  reduceMotionQuery.addEventListener("change", () => {
    createObserver();
    if (reduceMotionQuery.matches) {
      document.querySelectorAll<HTMLElement>(".mm-reveal").forEach(makeVisible);
      removePreloadGuard();
      return;
    }
    scheduleScan();
  });
}

// B22 fix. Several interactive Explorer pages (ExplorerPlayer, ExplorerTree,
// LessonQuestion, ChallengeFile) call window.history.pushState({ url }, ...)
// directly to reflect a sub-view change in the address bar. That overwrites the
// full page object Inertia keeps in history.state (which must include
// `component`, `props`, `version`, ...). When the user then follows an external
// link and later presses Back, the browser performs a fresh "back_forward"
// document load and Inertia's handleBackForwardVisit() reads history.state and
// calls resolveComponent(state.component). With the corrupted state,
// `component` is undefined, resolution throws, nothing mounts, and only the
// dark body background (--off-black, #181b1f) is left on screen — the reported
// all-black page. If the restored state is a corrupted (component-less) object,
// drop it so Inertia falls back to handleInitialPageVisit() and renders the
// server-provided page for this URL instead of a black screen.
function guardCorruptHistoryState() {
  const state: unknown = window.history.state;
  if (
    state !== null &&
    typeof state === "object" &&
    !("component" in state)
  ) {
    window.history.replaceState(null, "", window.location.href);
  }
}

// Safety net defensivo: algunas páginas montan modales/tutoriales en
// <Teleport to="body"> envueltos en <Transition>. Si una navegación con <Link>
// desmonta la página mientras la transición de salida sigue viva, el velo fijo
// (position:fixed; inset:0; z-index:9998) puede quedar HUÉRFANO en <body>,
// bloqueando el scroll. Tras cada navegación retiramos cualquier velo colgante
// del body (la página destino aún no ha abierto ningún modal, así que es seguro)
// y restauramos el scroll.
// NOTA: esto NO es el bug del "logo→modal". Ese es el modal de respuesta de
// Inertia ante una respuesta cacheada sin cabecera X-Inertia — lo maneja el
// handler `router.on("invalid", ...)` de más abajo.
function cleanupOrphanedOverlays() {
  document
    .querySelectorAll(
      "body > .modal-mask, body > .tutorial-overlay, body > .video-container",
    )
    .forEach((node) => node.remove());
  document.body.style.removeProperty("overflow");
  document.body.classList.remove("overflow-hidden");
}

async function initializeApp() {
  guardCorruptHistoryState();
  await createInertiaApp({
    progress: {
      color: "#EC3FDA",
    },
    resolve: (name) => {
      const pages = import.meta.glob<DefineComponent>(["@/pages/**/*.vue"]);

      const component = pages[`/website/frontend/pages/${name}.vue`];

      return component();
    },
    setup({ el, App, props, plugin }) {
      createApp({ render: () => h(App, props) })
        .use(plugin)
        .mount(el);
      setupGlobalReveal();
    },
  });

  // Retira velos de modal huérfanos tras cada navegación (safety net defensivo).
  router.on("navigate", cleanupOrphanedOverlays);

  // Bug 12 (el logo "abre la web dentro de un modal"). Cuando una visita de
  // Inertia recibe una respuesta que NO es Inertia, Inertia por defecto vuelca
  // ese HTML en un <iframe> modal fullscreen. En producción esto ocurre porque
  // Cloudflare sirve el HTML completo de `/` cacheado (cf-cache-status: HIT,
  // vary sin `X-Inertia`) a la XHR del <Link href="/">, sin la cabecera
  // `x-inertia`. En vez del modal, para respuestas OK (2xx) hacemos una
  // navegación completa a la URL destino: el HTML cacheado es una página válida
  // y se renderiza correctamente. Para errores reales (4xx/5xx) dejamos el
  // comportamiento por defecto (el modal es útil para depurar).
  router.on("invalid", (event) => {
    const response = (event as CustomEvent).detail?.response as
      | { status?: number; request?: { responseURL?: string }; config?: { url?: string } }
      | undefined;
    const status = response?.status ?? 0;
    if (status >= 200 && status < 300) {
      event.preventDefault();
      const url =
        response?.request?.responseURL ||
        response?.config?.url ||
        window.location.href;
      window.location.assign(url);
    }
  });
}

initializeApp().catch((e) => {
  console.error("Failed to initialize app:", e);
});
